// src/components/BookingForm.jsx
import React, { useState } from "react";
import ScheduleIcon from '@mui/icons-material/Schedule';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import LanguageIcon from '@mui/icons-material/Language';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import '../styles/Booking.css'; 

const BookingForm = ({ formData = {}, setFormData, onContinue, onBack, onClose, onCheckout }) => {
  const initialData = {
    name: '',
    email: '',
    phone: '',
    address: '',
    cap: '',
    city: '',
    province: '',
    country: 'Italia',
    fiscalCode: '',
    mode: '',
    clientType: '',
    note: '',
    ...formData
  };
  const [localFormData, setLocalFormData] = useState(initialData);
  const [accepted, setAccepted] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setLocalFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
    if (typeof setFormData === 'function') {
      setFormData((prevData) => ({
        ...prevData,
        [name]: value,
      }));
    }
  };

  const handleSubmit = async (e) => {
    if (e && typeof e.preventDefault === "function") e.preventDefault();

    const requiredFields = ['name', 'email', 'phone', 'address', 'cap', 'city', 'province', 'mode', 'clientType', 'note'];
    for (const field of requiredFields) {
      if (!localFormData[field] || localFormData[field].trim() === '') {
        alert(`Per favore, compila il campo obbligatorio: ${field}`);
        return;
      }
    }

    if (!accepted) {
      alert("Devi accettare l'informativa sulla privacy.");
      return;
    }

    // Check 24-hour minimum booking restriction
    if (formData.selectedDate && formData.selectedTime?.raw) {
      const [startTimeStr] = formData.selectedTime.raw.split(' - ');
      const selectedDateStr = new Date(formData.selectedDate).toISOString().split('T')[0];
      const appointmentDateTime = new Date(`${selectedDateStr}T${startTimeStr}:00`);
      const now = new Date();
      const timeDifference = appointmentDateTime.getTime() - now.getTime();
      const hoursDifference = timeDifference / (1000 * 60 * 60);

      if (hoursDifference < 24) {
        alert("La prenotazione deve essere effettuata con almeno 24 ore di anticipo rispetto all'orario selezionato.");
        return;
      }
    }

    if (typeof onContinue === "function") {
      console.log("Form submitted. Calling onContinue...");
      const fullFormData = {
        name: localFormData.name,
        email: localFormData.email,
        phone: localFormData.phone,
        address: localFormData.address,
        cap: localFormData.cap,
        city: localFormData.city,
        province: localFormData.province,
        country: localFormData.country,
        codiceFiscale: localFormData.fiscalCode,
        modalita: localFormData.mode,
        clientType: localFormData.clientType,
        motivo: localFormData.note,
        selectedDate: formData.selectedDate instanceof Date
          ? formData.selectedDate.toISOString().split("T")[0]
          : formData.selectedDate?.split("T")?.[0] || '',
        selectedTime: formData.selectedTime?.raw || '',
      };

      // Construct startDateTime and endDateTime in ISO format if possible
      let startDateTime = '';
      let endDateTime = '';

      if (
        formData.selectedDate &&
        typeof formData.selectedTime?.raw === 'string' &&
        formData.selectedTime.raw.includes(' - ')
      ) {
        const [startTimeStr, endTimeStr] = formData.selectedTime.raw.split(' - ');
        const selectedDateStr = new Date(formData.selectedDate).toISOString().split('T')[0];
        const startDate = new Date(`${selectedDateStr}T${startTimeStr}:00`);
        const endDate = new Date(`${selectedDateStr}T${endTimeStr}:00`);

        if (!isNaN(startDate) && !isNaN(endDate)) {
          startDateTime = startDate.toISOString();
          endDateTime = endDate.toISOString();
        }

        console.log("🕓 Constructed startDateTime:", startDateTime);
        console.log("🕓 Constructed endDateTime:", endDateTime);
      }

      fullFormData.startDateTime = startDateTime;
      fullFormData.endDateTime = endDateTime;

      setFormData(fullFormData);
      onContinue();
      return;
    }
  };

  return (
    <>
    <form
      className="w-full max-w-3xl mx-auto mt-8 bg-white/70 backdrop-blur-md p-8 rounded-2xl shadow-2xl border border-white/40 transition-all duration-500 ease-in-out transform relative"
      onSubmit={handleSubmit}
    >
      <div className="mb-6 border rounded-lg p-4 bg-gray-50">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 text-xs text-gray-700">
          {/* First Column */}
          <div className="flex flex-col gap-4">
            <div className="flex items-center">
              <ScheduleIcon className="text-[#304563] mr-2" />
              <span>
                {formData.selectedTime && formData.selectedDate ? (
                  <>
                    {formData.selectedTime?.raw} |{" "}
                    {new Date(formData.selectedDate).toLocaleDateString("it-IT", {
                      weekday: "short",
                      day: "2-digit",
                      month: "short",
                      year: "numeric",
                    })}
                  </>
                ) : (
                  "Orario e data non selezionati"
                )}
              </span>
            </div>
            <div className="flex items-center">
              <LocationOnIcon className="text-[#304563] mr-2" />
              <span>Via Plinio, 1, 20129 Milano MI, Italia</span>
            </div>
          </div>

          {/* Second Column */}
          <div className="flex flex-col gap-4">
            <div className="flex items-center">
              <LanguageIcon className="text-[#304563] mr-2" />
              <span>Fuso orario: Europe/Rome</span>
            </div>
            <div className="flex items-center">
              <AccessTimeIcon className="text-[#304563] mr-2" />
              <span>Durata: 1 ora</span>
            </div>
          </div>
        </div>
      </div>
      <div className="bg-blue-100 text-blue-800 p-2 rounded text-sm mb-4">
        <strong>La prenotazione sarà confermata dopo il checkout.</strong><br />
        Riceverai un’e-mail di conferma con i dettagli dell’appuntamento dopo il check-out
      </div>
      {/* 
      <div className="mb-4 text-sm text-gray-700">
        <p><strong>Data selezionata:</strong> {formData.selectedDate ? new Date(formData.selectedDate).toLocaleDateString() : "Non selezionata"}</p>
        <p><strong>Orario selezionato:</strong> {formData.selectedTime?.raw || "Non selezionato"}</p>
      </div>
      */}
      

      {/* Standard Label and Input: Cognome e nome */}
      <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
        Nome e cognome <span className="text-red-500">*</span>
      </label>
      <input
        type="text"
        name="name"
        id="name"
        value={localFormData.name}
        onChange={handleChange}
        required
        placeholder="Inserisci il tuo nome e cognome"
        className="w-full p-3 rounded-lg shadow-sm bg-white/80 focus:outline-none focus:ring-2 focus:ring-blue-400 mb-5"
      />

      {/* Standard Label and Input: E-mail */}
      <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
        E-mail <span className="text-red-500">*</span>
      </label>
      <input
        type="email"
        name="email"
        id="email"
        value={localFormData.email}
        onChange={handleChange}
        required
        placeholder="Inserisci la tua email"
        className="w-full p-3 rounded-lg shadow-sm bg-white/80 focus:outline-none focus:ring-2 focus:ring-blue-400 mb-5"
      />

      {/* Standard Label and Input: Numero di telefono */}
      <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-1">
        Numero di telefono <span className="text-red-500">*</span>
      </label>
      <input
        type="tel"
        name="phone"
        id="phone"
        value={localFormData.phone}
        onChange={handleChange}
        required
        placeholder="Inserisci il tuo numero di telefono"
        className="w-full p-3 rounded-lg shadow-sm bg-white/80 focus:outline-none focus:ring-2 focus:ring-blue-400 mb-5"
      />

      {/* Address Section */}
      <div className="mb-5">
        {/* Indirizzo */}
        <label htmlFor="address" className="block text-sm font-medium text-gray-700 mb-1">
          Indirizzo <span className="text-red-500">*</span>
        </label>
        <input
          type="text"
          name="address"
          id="address"
          value={localFormData.address}
          onChange={handleChange}
          required
          placeholder="Via/Piazza e Numero Civico"
          className="w-full p-3 rounded-lg shadow-sm bg-white/80 focus:outline-none focus:ring-2 focus:ring-blue-400 mb-4"
        />

        {/* CAP and Città in a row */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
          <div>
            <label htmlFor="cap" className="block text-sm font-medium text-gray-700 mb-1">
              CAP <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              name="cap"
              id="cap"
              value={localFormData.cap}
              onChange={handleChange}
              required
              placeholder="12345"
              pattern="[0-9]{5}"
              maxLength="5"
              className="w-full p-3 rounded-lg shadow-sm bg-white/80 focus:outline-none focus:ring-2 focus:ring-blue-400"
            />
          </div>
          <div>
            <label htmlFor="city" className="block text-sm font-medium text-gray-700 mb-1">
              Città <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              name="city"
              id="city"
              value={localFormData.city}
              onChange={handleChange}
              required
              placeholder="Inserisci la città"
              className="w-full p-3 rounded-lg shadow-sm bg-white/80 focus:outline-none focus:ring-2 focus:ring-blue-400"
            />
          </div>
        </div>

        {/* Provincia and Stato in a row */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label htmlFor="province" className="block text-sm font-medium text-gray-700 mb-1">
              Provincia <span className="text-red-500">*</span>
            </label>
            <select
              name="province"
              id="province"
              value={localFormData.province}
              onChange={handleChange}
              required
              className="w-full p-3 rounded-lg shadow-sm bg-white/80 focus:outline-none focus:ring-2 focus:ring-blue-400"
            >
              <option value="">Seleziona Provincia</option>
              <option value="AG">Agrigento (AG)</option>
              <option value="AL">Alessandria (AL)</option>
              <option value="AN">Ancona (AN)</option>
              <option value="AO">Aosta (AO)</option>
              <option value="AR">Arezzo (AR)</option>
              <option value="AP">Ascoli Piceno (AP)</option>
              <option value="AT">Asti (AT)</option>
              <option value="AV">Avellino (AV)</option>
              <option value="BA">Bari (BA)</option>
              <option value="BT">Barletta-Andria-Trani (BT)</option>
              <option value="BL">Belluno (BL)</option>
              <option value="BN">Benevento (BN)</option>
              <option value="BG">Bergamo (BG)</option>
              <option value="BI">Biella (BI)</option>
              <option value="BO">Bologna (BO)</option>
              <option value="BZ">Bolzano (BZ)</option>
              <option value="BS">Brescia (BS)</option>
              <option value="BR">Brindisi (BR)</option>
              <option value="CA">Cagliari (CA)</option>
              <option value="CL">Caltanissetta (CL)</option>
              <option value="CB">Campobasso (CB)</option>
              <option value="CI">Carbonia-Iglesias (CI)</option>
              <option value="CE">Caserta (CE)</option>
              <option value="CT">Catania (CT)</option>
              <option value="CZ">Catanzaro (CZ)</option>
              <option value="CH">Chieti (CH)</option>
              <option value="CO">Como (CO)</option>
              <option value="CS">Cosenza (CS)</option>
              <option value="CR">Cremona (CR)</option>
              <option value="KR">Crotone (KR)</option>
              <option value="CN">Cuneo (CN)</option>
              <option value="EN">Enna (EN)</option>
              <option value="FM">Fermo (FM)</option>
              <option value="FE">Ferrara (FE)</option>
              <option value="FI">Firenze (FI)</option>
              <option value="FG">Foggia (FG)</option>
              <option value="FC">Forlì-Cesena (FC)</option>
              <option value="FR">Frosinone (FR)</option>
              <option value="GE">Genova (GE)</option>
              <option value="GO">Gorizia (GO)</option>
              <option value="GR">Grosseto (GR)</option>
              <option value="IM">Imperia (IM)</option>
              <option value="IS">Isernia (IS)</option>
              <option value="SP">La Spezia (SP)</option>
              <option value="LT">Latina (LT)</option>
              <option value="LE">Lecce (LE)</option>
              <option value="LC">Lecco (LC)</option>
              <option value="LI">Livorno (LI)</option>
              <option value="LO">Lodi (LO)</option>
              <option value="LU">Lucca (LU)</option>
              <option value="MC">Macerata (MC)</option>
              <option value="MN">Mantova (MN)</option>
              <option value="MS">Massa-Carrara (MS)</option>
              <option value="MT">Matera (MT)</option>
              <option value="VS">Medio Campidano (VS)</option>
              <option value="ME">Messina (ME)</option>
              <option value="MI">Milano (MI)</option>
              <option value="MO">Modena (MO)</option>
              <option value="MB">Monza e Brianza (MB)</option>
              <option value="NA">Napoli (NA)</option>
              <option value="NO">Novara (NO)</option>
              <option value="NU">Nuoro (NU)</option>
              <option value="OG">Ogliastra (OG)</option>
              <option value="OT">Olbia-Tempio (OT)</option>
              <option value="OR">Oristano (OR)</option>
              <option value="PD">Padova (PD)</option>
              <option value="PA">Palermo (PA)</option>
              <option value="PR">Parma (PR)</option>
              <option value="PV">Pavia (PV)</option>
              <option value="PG">Perugia (PG)</option>
              <option value="PU">Pesaro e Urbino (PU)</option>
              <option value="PE">Pescara (PE)</option>
              <option value="PC">Piacenza (PC)</option>
              <option value="PI">Pisa (PI)</option>
              <option value="PT">Pistoia (PT)</option>
              <option value="PN">Pordenone (PN)</option>
              <option value="PZ">Potenza (PZ)</option>
              <option value="PO">Prato (PO)</option>
              <option value="RG">Ragusa (RG)</option>
              <option value="RA">Ravenna (RA)</option>
              <option value="RC">Reggio Calabria (RC)</option>
              <option value="RE">Reggio Emilia (RE)</option>
              <option value="RI">Rieti (RI)</option>
              <option value="RN">Rimini (RN)</option>
              <option value="RM">Roma (RM)</option>
              <option value="RO">Rovigo (RO)</option>
              <option value="SA">Salerno (SA)</option>
              <option value="SS">Sassari (SS)</option>
              <option value="SV">Savona (SV)</option>
              <option value="SI">Siena (SI)</option>
              <option value="SR">Siracusa (SR)</option>
              <option value="SO">Sondrio (SO)</option>
              <option value="TA">Taranto (TA)</option>
              <option value="TE">Teramo (TE)</option>
              <option value="TR">Terni (TR)</option>
              <option value="TO">Torino (TO)</option>
              <option value="TP">Trapani (TP)</option>
              <option value="TN">Trento (TN)</option>
              <option value="TV">Treviso (TV)</option>
              <option value="TS">Trieste (TS)</option>
              <option value="UD">Udine (UD)</option>
              <option value="VA">Varese (VA)</option>
              <option value="VE">Venezia (VE)</option>
              <option value="VB">Verbano-Cusio-Ossola (VB)</option>
              <option value="VC">Vercelli (VC)</option>
              <option value="VR">Verona (VR)</option>
              <option value="VV">Vibo Valentia (VV)</option>
              <option value="VI">Vicenza (VI)</option>
              <option value="VT">Viterbo (VT)</option>
            </select>
          </div>
          <div>
            <label htmlFor="country" className="block text-sm font-medium text-gray-700 mb-1">
              Stato
            </label>
            <input
              type="text"
              name="country"
              id="country"
              value={localFormData.country}
              onChange={handleChange}
              placeholder="Italia"
              className="w-full p-3 rounded-lg shadow-sm bg-white/80 focus:outline-none focus:ring-2 focus:ring-blue-400"
            />
          </div>
        </div>
      </div>

      {/* Standard Label and Input: Codice fiscale */}
      <label htmlFor="fiscalCode" className="block text-sm font-medium text-gray-700 mb-1">
        Codice fiscale
      </label>
      <input
        type="text"
        name="fiscalCode"
        id="fiscalCode"
        value={localFormData.fiscalCode}
        onChange={handleChange}
        placeholder="Se non hai il Codice Fiscale, lascia il campo vuoto"
        className="w-full p-3 rounded-lg shadow-sm bg-white/80 focus:outline-none focus:ring-2 focus:ring-blue-400 mb-5"
      />

      <div className="grid grid-cols-2 gap-4 mb-4">
        <div>
          <label className="block text-sm font-medium mb-1">
          Modalità<span className="text-red-500">*</span>
          </label>
          <select
            name="mode"
            onChange={handleChange}
            value={localFormData.mode}
            className="w-full p-3 rounded-lg shadow-sm bg-white/80 focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm"
            required
          >
            <option value="">Seleziona</option>
            <option value="studio">In Studio</option>
            <option value="zoom">Zoom</option>
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium mb-1">
           Cliente<span className="text-red-500">*</span>
          </label>
          <select
            name="clientType"
            onChange={handleChange}
            value={localFormData.clientType}
            className="w-full p-3 rounded-lg shadow-sm bg-white/80 focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm"
            required
          >
            <option value="">Seleziona</option>
            <option value="privato / forfettario">Privato / Forfettario</option>
            <option value="Azienda / P.Iva">Azienda / P.Iva</option>
          </select>
        </div>
      </div>

      {/* Standard Label and Textarea: Motivo dell’appuntamento */}
      <label htmlFor="note" className="block text-sm font-medium text-gray-700 mb-1">
        Motivo dell’appuntamento <span className="text-red-500">*</span>
      </label>
      <textarea
        name="note"
        id="note"
        value={localFormData.note}
        onChange={handleChange}
        required
        placeholder="Scrivi il motivo dell’appuntamento"
        className="w-full p-3 rounded-lg shadow-sm bg-white/80 focus:outline-none focus:ring-2 focus:ring-blue-400 mb-5 resize-none text-sm text-gray-900"
        rows={3}
      />

      <p className="text-xs text-gray-600 mb-4">
        I dati forniti saranno considerati confidenziali e protetti dal segreto professionale. Ti chiediamo di accettare comunque i Termini e le Condizioni nonché l’Informativa sulla Privacy.
      </p>
      <div className="privacy-section mb-4 flex items-center">
        <input
          type="checkbox"
          id="privacy"
          className="mr-2 rounded-md"
          checked={accepted}
          onChange={(e) => setAccepted(e.target.checked)}
        />
        <label htmlFor="privacy" className="text-sm text-gray-700 align-middle">
          Accetto l'<a href="https://studiogenise.com/policies/privacy-policy" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-800 underline">informativa sulla privacy</a> <span className="text-red-500">*</span>
        </label>
      </div>

      <div className="flex flex-col md:flex-row justify-between items-center gap-4 mt-4">
        <div className="text-center">
          <p className="text-gray-600 text-sm">Prezzo Solo Online</p>
          {localFormData.clientType === 'privato / forfettario' && (
            <>
              <p className="text-xl font-semibold text-[#304653]">€127,45</p>
              <p className="text-sm text-[#9ca3af]">Anziché €192,32</p>
            </>
          )}
          {localFormData.clientType === 'Azienda / P.Iva' && (
            <>
              <p className="text-xl font-semibold text-[#304653]">€175,54</p>
              <p className="text-sm text-[#9ca3af]">Anziché €267,20</p>
            </>
          )}
        </div>
        <div className="flex gap-4">
          <button
            type="button"
            onClick={(e) => {
              e.preventDefault();
              handleSubmit(e);
            }}
            className="bg-[#304653] text-white px-6 py-2 rounded-full shadow-md hover:bg-[#253844] font-semibold text-sm transition duration-300 transform active:scale-95"
          >
            Continua
          </button>
        </div>
      </div>
    </form>
    </>
  );
};

export default BookingForm;